/**
 * PROVISIONAL — USER TUNING REQUIRED.
 *
 * These values provide a bounded procedural Web Audio implementation for M4.
 * They are not accepted mix, timbre, cadence, or atmosphere settings. Tune
 * them with the user against the fixed Vilnius bookmarks.
 */
export const AUDIO_CONFIG = Object.freeze({
  masterGain: 0.5,
  maximumConcurrentOneShots: 12,
  rememberedEventIdLimit: 512,
  rememberedCooldownKeyLimit: 128,
  footstepCooldownTicks: 3,
  landingCooldownTicks: 8,
  muteSmoothingSeconds: 0.02,
  footstepDurationSeconds: 0.085,
  landingDurationSeconds: 0.18,
  bellDurationSeconds: 2.8,
  windLoopSeconds: 2.5,
  riverLoopSeconds: 3.5,
  interiorLoopSeconds: 4,
  vehicleEngineLoopSeconds: 1,
  vehicleTyreLoopSeconds: 2,
  vehicleSirenLoopSeconds: 1.52,
  vehicleHornLoopSeconds: 0.5,
  vehicleImpactDurationSeconds: 0.22,
  windGain: 0.18,
  riverGain: 0.2,
  interiorGain: Object.freeze({
    "cathedral-nave": 0.13,
    "airport-terminal": 0.078,
    "civic-lobby": 0.09,
    "food-hall": 0.095,
    "music-venue": 0.08,
    "railway-hall": 0.082,
    "bus-terminal": 0.086,
    "historic-bank": 0.072,
    "prison-cellblock": 0.075,
    "tower-lobby": 0.065,
    "tower-observation": 0.045,
  }),
  interiorBellGainScale: Object.freeze({
    "cathedral-nave": 0.48,
    "airport-terminal": 0.09,
    "civic-lobby": 0.16,
    "food-hall": 0.07,
    "music-venue": 0.06,
    "railway-hall": 0.12,
    "bus-terminal": 0.08,
    "historic-bank": 0.1,
    "prison-cellblock": 0.08,
    "tower-lobby": 0.11,
    "tower-observation": 0.24,
  }),
  windSeasonGainScale: Object.freeze({
    spring: 0.55,
    summer: 1,
    autumn: 0.8,
    winter: 0.95,
  }),
  riverSeasonGainScale: Object.freeze({
    spring: 1.05,
    summer: 1,
    autumn: 0.9,
    winter: 0.65,
  }),
  bellGain: 0.16,
  footstepGain: 0.28,
  landingGain: 0.36,
  vehicleEngineGain: 0.22,
  vehicleTyreGain: 0.2,
  vehicleSirenGain: 0.3,
  vehicleHornGain: 0.28,
  vehicleImpactGain: 0.38,
  vehicleLoopSmoothingSeconds: 0.055,
  vehicleEngineIdleLevel: 0.16,
  vehicleEngineMinimumPlaybackRate: 0.68,
  vehicleEnginePlaybackRateRange: 1.55,
  vehicleTyreMinimumSpeedMps: 2.5,
  vehicleImpactTrigger01: 0.08,
  vehicleEngineClassPitchScale: Object.freeze({
    car: 1,
    "service-van": 0.88,
    "fire-engine": 0.68,
    "city-bus": 0.72,
    trolleybus: 1.18,
  }),
  windElevationFloorMetres: 90,
  windElevationRangeMetres: 140,
  ambienceSmoothingSeconds: 0.18,
  bellStartHour: 6,
  bellEndHour: 23,
  bellSlotsPerHour: 4,
  noiseSampleRate: 22_050,
  surfaceProfiles: Object.freeze({
    terrain: Object.freeze({
      highpassHertz: 90,
      lowpassHertz: 1_850,
      playbackRate: 0.78,
      gainScale: 0.82,
      noiseMemory: 0.72,
    }),
    road: Object.freeze({
      highpassHertz: 650,
      lowpassHertz: 5_400,
      playbackRate: 1.12,
      gainScale: 0.9,
      noiseMemory: 0,
    }),
    buildingGround: Object.freeze({
      highpassHertz: 260,
      lowpassHertz: 2_900,
      playbackRate: 0.92,
      gainScale: 1,
      noiseMemory: 0.42,
    }),
  }),
  footstepVoice: Object.freeze({
    attackRate: 80,
    envelopeDecay: 7,
    pitchVariationStep: 0.006,
  }),
  landingVoice: Object.freeze({
    playbackRate: 0.82,
    pitchVariationStep: 0.008,
    highpassHertz: 45,
    lowpassHertz: 1_200,
    noiseMemory: 0.76,
    envelopeDecay: 10,
  }),
  bellVoice: Object.freeze({
    playbackRate: 0.94,
    pitchVariationStep: 0.006,
    highpassHertz: 120,
    lowpassHertz: 3_200,
    attackRate: 90,
    outputScale: 0.62,
    partials: Object.freeze([
      Object.freeze({ hertz: 261.6, amplitude: 0.52, decay: 2.2 }),
      Object.freeze({ hertz: 329.6, amplitude: 0.24, decay: 2.8 }),
      Object.freeze({ hertz: 392, amplitude: 0.18, decay: 3.4 }),
      Object.freeze({ hertz: 554.4, amplitude: 0.11, decay: 4.2 }),
    ]),
  }),
  foodHallVoice: Object.freeze({
    murmurGain: 0.72,
    sizzleGain: 0.05,
    sizzlePulseHertz: 0.8,
    espressoGain: 0.12,
    espressoStartSeconds: 0.72,
    espressoDurationSeconds: 0.62,
    clinkGain: 0.1,
    clinkDecay: 18,
    clinks: Object.freeze([
      Object.freeze({ atSeconds: 0.32, hertz: 1_360 }),
      Object.freeze({ atSeconds: 2.48, hertz: 1_720 }),
    ]),
  }),
  ambienceFilters: Object.freeze({
    windHertz: 780,
    vilniaHertz: 1_250,
    nerisHertz: 880,
    cathedralNaveHertz: 540,
    airportTerminalHertz: 205,
    civicLobbyHertz: 260,
    foodHallHertz: 780,
    musicVenueHertz: 175,
    railwayHallHertz: 185,
    busTerminalHertz: 168,
    historicBankHertz: 215,
    prisonCellblockHertz: 190,
    towerLobbyHertz: 310,
    towerObservationHertz: 640,
  }),
  ambienceNoiseMemory: Object.freeze({
    wind: 0.985,
    vilnia: 0.84,
    neris: 0.91,
    cathedralNave: 0.994,
    airportTerminal: 0.993,
    civicLobby: 0.988,
    foodHall: 0.982,
    musicVenue: 0.996,
    railwayHall: 0.995,
    busTerminal: 0.9935,
    historicBank: 0.997,
    prisonCellblock: 0.996,
    towerLobby: 0.991,
    towerObservation: 0.982,
  }),
});
